import {
  encodeClientWsMessage,
  REPLY_FAILED_ERROR_CODE,
  serverGameRoundSchema,
  serverGameSolutionSchema,
  serverSkillsCreditedSchema,
  type ServerWsMessage,
  type WebSearchSource,
} from '@orborix/shared-types';
import { MIN_PARTIAL_CHARS } from './constants.js';
import {
  clearKaraokeBuffer,
  pushAlignment,
  snapshotKaraokeAlignment,
  type KaraokeBufferState,
} from './karaokeBuffer.js';
import { closeSettleWindow, type SettleDropCounter } from './micSettleCounter.js';
import {
  applyCorrection,
  applyCorrectionsDone,
  applyGameAttempt,
  applySkillsCredited,
  applyPronunciationCorrection,
  applyPronunciationReport,
  applyTranslationToLastBot,
  appendOrReplaceToolResult,
  markCorrectionSpoken,
  pickQualifications,
  retireTurnAnchors,
} from './messageAnchors.js';
import type {
  AudioPlayerLike,
  ClientIOMode,
  GameRoundLive,
  GameSolutionLive,
  PronunciationCorrection,
  PronunciationReportLite,
  VoiceMessage,
  VoiceMessageCorrection,
  VoiceState,
} from './types.js';

/**
 * Deps que el handler de mensajes server necesita. Todas son refs o
 * setters estables — el handler se compone una sola vez por sesión.
 */
export interface ServerHandlerDeps {
  prevStateRef: { current: VoiceState };
  stateRef: { current: VoiceState };
  assistantTextRef: { current: string };
  pendingBotTranslationRef: { current: { text: string; targetLang: string } | null };
  pendingBotWebSourcesRef: { current: ReadonlyArray<WebSearchSource> | null };
  setLiveWebSources: (sources: ReadonlyArray<WebSearchSource>) => void;
  lastFinalTranscriptRef: { current: string | null };
  ttsStopwatchRef: { current: number | null };
  /**
   * Arma la ventana de settle (`MIC_SETTLE_MS`) desde ahora: el gate del pcm
   * handler y el pre-roll del worklet. Ver `useVoiceSession.armMicSettle`.
   */
  armMicSettle: () => void;
  /** Dedupe del awaitIdle que re-arma el settle tras el audio out-of-band (coda). */
  codaSettlePendingRef: { current: boolean };
  /** Diagnóstico de cuánto muerde el settle. Ver `micSettleCounter`. */
  settleCounterRef: { current: SettleDropCounter };
  /** ¿Llegó el `turn-end` del turno del coach en curso? Lo lee el `interrupt` (cuánto sonó). */
  turnAudioCompleteRef: { current: boolean };
  pausedRef: { current: boolean };
  ioModeRef: { current: ClientIOMode };
  pendingAcks: { current: Map<string, () => void> };
  wsRef: { current: WebSocket | null };
  playerRef: { current: AudioPlayerLike | null };
  karaoke: KaraokeBufferState;
  setState: (next: VoiceState) => void;
  setTranscript: (text: string) => void;
  setAssistantText: (text: string) => void;
  setMessages: (
    updater: (prev: ReadonlyArray<VoiceMessage>) => ReadonlyArray<VoiceMessage>,
  ) => void;
  setSessionId: (id: string | null) => void;
  /** The session id of the connection so far (`null` before the first `session.started`). */
  sessionIdRef: { readonly current: string | null };
  /** The live translate-it round (`game.round`, plan 47). */
  setGameRound: (round: GameRoundLive) => void;
  /** A solution the server sent (`game.solution`, plan 47 Round 2). */
  addGameSolution: (solution: GameSolutionLive) => void;
  setLastTtsFirstByteMs: (ms: number) => void;
  setIsAudioPlaying: (next: boolean) => void;
  setPaused: (paused: boolean) => void;
  setIoModeState: (next: ClientIOMode) => void;
  setTranslationEnabledState: (enabled: boolean) => void;
  setTranslationTargetLangState: (lang: string) => void;
  /** Setea si la sesión graba audio crudo (para gatear el play del audio real). */
  setAudioRecording: (on: boolean) => void;
  /**
   * Commit en vuelo (push-to-talk): true desde el tap de enviar (`turn-stop`)
   * hasta que el server confirma el `transcript.final` — o el turno muere. Lo
   * setea `commitTurn()`; acá sólo se LEE y se limpia.
   */
  commitPendingRef: { current: boolean };
  /** Limpia `commitPending` (transcript.final llegó, o error/cierre). */
  clearCommitPending: () => void;
  /**
   * El turno post-tap murió VACÍO (el server emitió el partial vacío de
   * limpieza con un commit en vuelo): el audio nunca llegó al provider o el
   * commit fue ignorado. La UI avisa "no se escuchó" y limpia el pending.
   */
  onTurnEmpty: () => void;
  /**
   * El reply del coach FALLÓ (`{type:'error', code:'reply_failed'}`): el
   * provider del LLM rechazó o se cortó. La sesión sigue viva — el server
   * cierra el turno con su `turn-end` + `listening` de siempre — así que NO es
   * `state:'error'`: la app muestra su aviso con Reintentar.
   */
  onReplyFailed: () => void;
  /** Llegó texto del coach: un aviso de fallo previo deja de valer. */
  onReplyText: () => void;
  /**
   * Modo realtime (S2S full-duplex): ref que gatea el bypass del gate
   * half-duplex del mic + setter de estado para que la app adapte su UX. El
   * server lo anuncia en `session.started { conversationMode }` (decisión #20
   * del plan realtime) — el cliente no lo elige.
   */
  realtimeModeRef: { current: boolean };
  /**
   * Modo realtime — id del ciclo de respuesta de OpenAI cuya burbuja del bot
   * está viva (F4b, decisión #16). Los `tokens`/`turn-end` viajan keyeados
   * por `responseId`: un delta con id NUEVO commitea la burbuja anterior
   * antes de abrir la propia (solape por barge-in), y un turn-end con id
   * viejo no pisa una burbuja más nueva (commit idempotente). En clásico
   * queda null (los mensajes no traen id y el karaoke maneja el revelado).
   */
  realtimeLiveResponseIdRef: { current: string | null };
  setConversationMode: (mode: 'realtime' | 'classic') => void;
  /**
   * ¿Esta sesión produce correcciones? Lo anuncia el server en
   * `session.started { corrections }` (el gate de montaje del side-runner:
   * off en el examen de nivel, en A0 sin misión y en módulos sin
   * `correctionsRunner`). En `false` NO marcamos `correctionPending` en los
   * user msgs: nunca va a llegar el `corrections.done` que lo limpia y el
   * skeleton "analizando…" quedaba colgado bajo cada burbuja (card #318).
   * Default `true` = conducta previa (backend viejo sin el campo).
   */
  correctionsEnabledRef: { current: boolean };
  /**
   * El server terminó la sesión por iniciativa propia — sea un cierre normal
   * (`session.ended`: end_interview de Scout, `idle-no-speech`) o un bloqueo
   * terminal (`session.blocked`: saldo agotado, safety L4 de coach). Marca el
   * cierre como INTENCIONAL en el hook: el close posterior del WS NO debe
   * disparar la auto-reconexión — reconectar sobre una fila finalizada crearía
   * sesiones fantasma en loop, y contra un bloqueo el preflight rebota los
   * reintentos hasta dejar la sesión en `lost`.
   */
  onServerSessionClosed: () => void;
}

export interface ServerMessageHandler {
  handleBinary: (data: ArrayBuffer) => void;
  handleJson: (msg: ServerWsMessage) => void;
}

/**
 * Construye el handler de mensajes del WS. La lógica completa del switch
 * vive acá; el hook orquestador sólo le pasa las refs/setters necesarias.
 *
 * El handler es stateless en sí mismo — todo el estado vive en los refs
 * que recibe. Esto permite testearlo con jsdom + refs falsos.
 */
export function createServerMessageHandler(deps: ServerHandlerDeps): ServerMessageHandler {
  const handleBinary = (data: ArrayBuffer): void => {
    // TTS first-byte: medimos del momento en que entramos a `thinking` al
    // primer chunk de audio. Proxy más limpio para detectar degradación.
    if (deps.ttsStopwatchRef.current != null) {
      const elapsed = Date.now() - deps.ttsStopwatchRef.current;
      deps.ttsStopwatchRef.current = null;
      deps.setLastTtsFirstByteMs(elapsed);
      // Fase 36 — anclar karaoke al inicio del turno con ctx.currentTime
      // capturado ANTES del push (= momento exacto en que empieza a sonar).
      deps.karaoke.audioStartedAtCtxMs = deps.playerRef.current?.getClockMs?.() ?? 0;
      // Turno nuevo del coach: desde acá se cuenta cuánto suena (por si lo cortan).
      deps.playerRef.current?.markTurnStart();
      deps.turnAudioCompleteRef.current = false;
      if (deps.karaoke.pendingAlignments.length > 0) {
        for (const chunk of deps.karaoke.pendingAlignments) {
          pushAlignment(deps.karaoke, chunk);
        }
        deps.karaoke.pendingAlignments = [];
      }
    }
    deps.playerRef.current?.push(data);
    deps.setIsAudioPlaying(true);

    // Audio OUT-OF-BAND (coda hablada de correcciones): llega por este mismo
    // stream binario, pero el server suprime sus `state`/`turn-end`, así que
    // NINGUNA de las dos vías que arman el settle (handleTurnEnd y la
    // transición speaking/thinking→listening) se dispara. Sin re-armar el
    // settle, al terminar el audio de la coda `player.isPlaying()` cae a false y
    // el gate del mic reenvía PCM de inmediato → el reverb/decay de la coda
    // entra al STT como un turno fantasma (justo lo que MIC_SETTLE_MS evita).
    // Detectamos out-of-band por estado: el audio del reply llega en
    // 'speaking'/'thinking'; el de la coda, en 'listening' (el reply ya cerró).
    // Disparamos UN solo awaitIdle (dedupe por flag) que re-arma el settle desde
    // el FIN ACÚSTICO real de la coda — mismo mecanismo que handleTurnEnd.
    const st = deps.stateRef.current;
    if (st !== 'speaking' && st !== 'thinking' && !deps.codaSettlePendingRef.current) {
      const player = deps.playerRef.current;
      if (player?.awaitIdle) {
        deps.codaSettlePendingRef.current = true;
        void player.awaitIdle().then(() => {
          deps.codaSettlePendingRef.current = false;
          deps.armMicSettle();
          deps.setIsAudioPlaying(false);
        });
      }
    }
  };

  const handleJson = (msg: ServerWsMessage): void => {
    switch (msg.type) {
      case 'state':
        return handleState(deps, msg);
      case 'transcript':
        return handleTranscript(deps, msg);
      case 'tokens':
        return handleTokens(deps, msg);
      case 'tokens.done':
        return handleTokensDone(deps);
      case 'turn-end':
        return handleTurnEnd(deps, msg);
      case 'interrupted':
        return handleInterrupted(deps);
      case 'interrupt-ack': {
        const resolver = deps.pendingAcks.current.get(msg.interruptionId);
        if (resolver) {
          deps.pendingAcks.current.delete(msg.interruptionId);
          resolver();
        }
        return;
      }
      case 'error':
        // Un commit en vuelo no va a resolverse — que la UI no espere.
        deps.clearCommitPending();
        if (msg.code === REPLY_FAILED_ERROR_CODE) {
          deps.onReplyFailed();
          return;
        }
        deps.setState('error');
        return;
      case 'session.started':
        return handleSessionStarted(deps, msg);
      case 'session.cost.updated':
        return dispatchWindowEvent('orborix:session.cost.updated', {
          sessionId: msg.sessionId,
          turnId: msg.turnId,
          deltaNano: msg.deltaNano,
          totalNano: msg.totalNano,
        });
      case 'session.blocked':
        // Bloqueo TERMINAL del server (saldo agotado → close 4402; safety L4 de
        // coach → 4403): el close que sigue es deliberado. Se marca ACÁ, antes
        // de que llegue, porque reintentar contra un backend que ya bloqueó
        // agota el presupuesto y deja la sesión en `lost` — tapando el copy del
        // bloqueo. Es además la ÚNICA vía para el 4403 (el close handler sólo
        // reconoce el 4402). Las apps conservan su listener del window event
        // para ese copy (y su `stop()`, que sigue siendo idempotente).
        deps.onServerSessionClosed();
        return dispatchWindowEvent('orborix:session.blocked', {
          reason: msg.reason,
          level: msg.level,
          balanceNano: msg.balanceNano,
        });
      case 'session.safety':
        // Safety router de coach (niveles 1-3): la app decide la UI (card de
        // ayuda humana / presencia de contención) — el hook no acopla eso.
        return dispatchWindowEvent('orborix:session.safety', {
          sessionId: msg.sessionId,
          level: msg.level,
          intervention: msg.intervention,
          resources: msg.resources,
        });
      case 'session.ended':
        // El server terminó la sesión por iniciativa propia (Scout:
        // `end_interview`; idle-no-speech). Lo redisparamos como window event
        // para que la app transicione a su pantalla de cierre sin acoplar el
        // hook de voz a esa feature. El WS sigue abierto hasta que el TTS de
        // cierre termina (Scout) o hasta que el server lo cierre (idle).
        // `onServerSessionClosed` marca el cierre como intencional: el close
        // posterior NO reconecta.
        deps.onServerSessionClosed();
        return dispatchWindowEvent('orborix:session.ended', { reason: msg.reason });
      case 'session.closing':
        // Preaviso del cierre por inactividad (countdown accionable en la UI).
        return dispatchWindowEvent('orborix:session.closing', {
          inSeconds: msg.inSeconds,
          reason: msg.reason,
        });
      case 'session.closing.cancelled':
        // El user dio señal de vida post-preaviso → la UI retira el countdown.
        return dispatchWindowEvent('orborix:session.closing.cancelled', {});
      case 'video.error':
        return dispatchWindowEvent('orborix:video.error', {
          reason: msg.reason,
          message: msg.message,
        });
      case 'correction': {
        const correction: VoiceMessageCorrection = {
          said: msg.said,
          fix: msg.fix,
          explanation: msg.explanation ?? null,
          ...(msg.severity ? { severity: msg.severity } : {}),
          ...(msg.turnIndex !== undefined ? { turnIndex: msg.turnIndex } : {}),
          ...(msg.skillIds && msg.skillIds.length > 0 ? { skillIds: msg.skillIds } : {}),
        };
        deps.setMessages((m) => applyCorrection(m, correction, msg.userText ?? null));
        return;
      }
      case 'corrections.done':
        // The turn's verdict lands on ITS bubble (by `turnIndex` once the bubble
        // knows it, else by `userText` like the chips): only `ok` means its
        // empty chip list is a clean sentence.
        deps.setMessages((m) =>
          applyCorrectionsDone(m, {
            turnIndex: msg.turnIndex,
            userText: msg.userText,
            verdict: msg.verdict,
          }),
        );
        return;
      case 'skills.credited': {
        // W5: the catalog skills this turn showed, stored on ITS bubble
        // (by turn index, then `userText`, like the verdict). Progress is
        // silent by design; the app decides what to celebrate — reasoning over
        // these fields (a nailed focus's `priorTurns`), so the message is
        // validated here and a payload that does not match (an older server)
        // is dropped loudly, never half-read.
        const parsed = serverSkillsCreditedSchema.safeParse(msg);
        if (!parsed.success) {
          console.warn('[voice] skills.credited does not match the contract — credits dropped', {
            turnIndex: msg.turnIndex,
            issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
          });
          return;
        }
        const { turnIndex, userText, credits } = parsed.data;
        deps.setMessages((m) => applySkillsCredited(m, { turnIndex, userText, credits }));
        return;
      }
      case 'game.round': {
        // Plan 47: the translate-it round. Validated like `skills.credited`
        // (the app reasons over these fields; a payload that does not match —
        // an older server — is dropped loudly, never half-read). The session
        // state feeds the challenge card; a verdict also lands on ITS bubble.
        const parsed = serverGameRoundSchema.safeParse(msg);
        if (!parsed.success) {
          console.warn('[voice] game.round does not match the contract — dropped', {
            issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
          });
          return;
        }
        const { event, meaningCheck, attempt, seed, round, challenge, attemptNo, rematch } =
          parsed.data;
        const snapshot = { seed, round, challenge, attemptNo, rematch };
        // Absent (an older server) reads as unknown.
        const band = parsed.data.band ?? null;
        deps.setGameRound({
          ...snapshot,
          event,
          meaningCheck,
          band,
          bandShift: parsed.data.bandShift ?? null,
          challengeSpan: parsed.data.challengeSpan ?? null,
          suggestHarder: parsed.data.suggestHarder === true,
        });
        if (attempt) {
          deps.setMessages((m) =>
            applyGameAttempt(m, {
              turnIndex: attempt.turnIndex,
              userText: attempt.userText,
              attempt: {
                ...snapshot,
                verdict: attempt.verdict,
                // Absent = a server older than the field: the whole turn.
                judgedText: attempt.judgedText ?? null,
                band,
              },
            }),
          );
        }
        return;
      }
      case 'game.solution': {
        // Plan 47 Round 2: the answer to «No sé» / «Ver solución». Validated
        // like `game.round`; a payload that does not match is dropped loudly.
        const parsed = serverGameSolutionSchema.safeParse(msg);
        if (!parsed.success) {
          console.warn('[voice] game.solution does not match the contract — dropped', {
            issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
          });
          return;
        }
        const { type: _type, ...solution } = parsed.data;
        deps.addGameSolution(solution);
        return;
      }
      case 'correction.spoken':
        // La coda del modo manos libres DIJO esta corrección en voz alta —
        // badge en el chip. El audio de la coda viaja por el stream binario
        // normal (sin state/turn-end propios), así que este marker es la única
        // señal explícita de la coda que ve el cliente.
        deps.setMessages((m) => markCorrectionSpoken(m, msg.said, msg.fix));
        return;
      case 'objective.progress':
        // Práctica de Escenarios (B6) + Programa con sub-unidades (17.B):
        // progreso de objetivo en vivo. Lo redisparamos como window event para
        // que el tracker lo consuma sin acoplar el hook de voz a la feature.
        // `count`/`reps` presentes sólo en objetivos de sub-unidad (anillo).
        return dispatchWindowEvent('orborix:objective.progress', {
          objectiveId: msg.objectiveId,
          met: msg.met,
          evidence: msg.evidence,
          turnIndex: msg.turnIndex,
          count: msg.count,
          reps: msg.reps,
          // Charla asistida: practicado con la respuesta modelo, no cumplido.
          assisted: msg.assisted === true,
        });
      case 'assisted.result':
        // Charla asistida (encargo 99): el veredicto del server sobre lo que el
        // alumno dijo contra la respuesta modelo. Rechazado = el turno NO llegó
        // al coach: no habrá `transcript.final`, así que el envío en vuelo se
        // cierra acá (sin el aviso de «no llegó»: la tarjeta dice qué pasó).
        // Aceptado: el final llega detrás y cierra el envío como siempre.
        if (!msg.accepted) {
          deps.clearCommitPending();
          deps.setTranscript('');
        }
        return dispatchWindowEvent('orborix:assisted.result', msg);
      case 'a0.suggestions':
        // Red A0 (plan 33 §1.1): chips tap-or-speak del objetivo pendiente. Lo
        // redisparamos como window event para `useA0Suggestions` sin acoplar el
        // hook de voz a la feature (mismo patrón que objective.progress).
        return dispatchWindowEvent('orborix:a0.suggestions', {
          objectiveId: msg.objectiveId,
          goalEs: msg.goalEs,
          stage: msg.stage,
          suggestions: msg.suggestions,
        });
      case 'a0.mission.complete':
        // Misión A0 completa (plan 33 §1.3): todos los objetivos cumplidos. El
        // front la usa para el boss (gatea graduación) / festejo de micro-charla.
        return dispatchWindowEvent('orborix:a0.mission.complete', {});
      case 'lesson.playback.await': {
        const acknowledge = (failed: boolean) => {
          const ws = deps.wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          if (!failed) commitLiveBotBubble(deps);
          try { ws.send(encodeClientWsMessage({ type: 'playback-finished', lessonReceiptId: msg.receiptId, ...(failed ? { lessonPlaybackFailed: true } : {}) })); } catch { /* server timeout/reconnect recovers */ }
        };
        void (deps.playerRef.current?.awaitIdle?.() ?? Promise.resolve()).then(() => acknowledge(false), () => acknowledge(true));
        return;
      }
      case 'lesson.completed':
        return dispatchWindowEvent('orborix:lesson.completed', msg);
      case 'subunit.advanced':
        // Programa con sub-unidades (17.B): el server fija la sub-unidad activa
        // de la sesión. El controller arma el tracker de reps.
        return dispatchWindowEvent('orborix:subunit.advanced', {
          unitId: msg.unitId,
          subUnitId: msg.subUnitId,
          order: msg.order,
          total: msg.total,
          titleEs: msg.titleEs,
          titleEn: msg.titleEn,
          ...(msg.blurbEs !== undefined ? { blurbEs: msg.blurbEs } : {}),
          // Sin esto el rótulo de REPASO del tracker nunca se prendía en vivo:
          // el controller lo lee del detail y el detail no lo traía.
          ...(msg.review !== undefined ? { review: msg.review } : {}),
          objectives: msg.objectives,
          isFinal: msg.isFinal,
          // Habilita el festejo de unidad: completar ESTA sub-unidad la domina.
          ...(msg.completesUnit !== undefined ? { completesUnit: msg.completesUnit } : {}),
          initial: msg.initial,
        });
      case 'pronunciation_report':
        return handlePronunciationReport(deps, msg);
      case 'pronunciation_correction':
        return handlePronunciationCorrection(deps, msg);
      case 'session.paused':
        deps.pausedRef.current = true;
        deps.setPaused(true);
        return;
      case 'session.resumed':
        deps.pausedRef.current = false;
        deps.setPaused(false);
        return;
      case 'mode.changed':
        deps.ioModeRef.current = msg.mode;
        deps.setIoModeState(msg.mode);
        return;
      case 'translation.state':
        deps.setTranslationEnabledState(msg.enabled);
        deps.setTranslationTargetLangState(msg.targetLang.toLowerCase());
        return;
      case 'llm.translation': {
        const translation = {
          text: msg.translatedText,
          targetLang: msg.targetLang.toLowerCase(),
        };
        // El bot turn puede no estar todavía committeado. Si hay
        // assistantText vivo, stash; si no, aplicamos al último bot msg.
        if (deps.assistantTextRef.current) {
          deps.pendingBotTranslationRef.current = translation;
          return;
        }
        deps.setMessages((m) => applyTranslationToLastBot(m, translation));
        return;
      }
      case 'llm.text':
        // Ignored — el stream de texto llega vía `tokens`.
        return;
      case 'tts.alignment':
        pushAlignment(deps.karaoke, {
          chars: msg.chars,
          charStartTimesMs: msg.charStartTimesMs,
          charDurationsMs: msg.charDurationsMs,
          audioStartOffsetMs: msg.audioStartOffsetMs,
        });
        return;
      case 'tool.result': {
        const next: VoiceMessage = {
          who: 'tool',
          text: msg.text,
          content: msg.content,
          toolCallId: msg.toolCallId,
          toolName: msg.name,
        };
        deps.setMessages((m) => appendOrReplaceToolResult(m, next));
        return;
      }
      case 'web_search.started':
        // El coach arrancó una búsqueda web nativa. Lo redisparamos como window
        // event para que la app muestre "Navegando la web…" sin acoplar el hook
        // de voz a esa feature (mismo patrón que objective.progress).
        return dispatchWindowEvent('orborix:web_search.started', {
          query: msg.query ?? null,
        });
      case 'web_search.sources':
        // Las fuentes se anclan a la burbuja del bot de ESTE turno (se consumen
        // en `commitLiveBotBubble`), no como evento suelto — así la cita queda
        // bajo el mensaje que la generó y scrollea con él.
        deps.pendingBotWebSourcesRef.current = msg.sources;
        return;
      case 'fit.set.logged':
        // Fit (F2): el extractor por voz registró una serie del workout. La app de
        // fit lo consume para tildar el set en vivo + avanzar/cerrar el ejercicio.
        return dispatchWindowEvent('orborix:fit.set.logged', {
          workoutId: msg.workoutId,
          exerciseId: msg.exerciseId,
          setNumber: msg.setNumber,
          reps: msg.reps,
          kg: msg.kg,
          rir: msg.rir ?? null,
          source: msg.source,
          exerciseCompleted: msg.exerciseCompleted,
          workoutCompleted: msg.workoutCompleted,
        });
      case 'fit.substitution.applied':
        // Fit (F2): se aplicó una sustitución por voz ("la barra está ocupada").
        return dispatchWindowEvent('orborix:fit.substitution.applied', {
          workoutId: msg.workoutId,
          fromExerciseId: msg.fromExerciseId,
          toExerciseId: msg.toExerciseId,
          toName: msg.toName,
          reason: msg.reason ?? null,
        });
      case 'fit.pr':
        // Fit (F2): la serie recién registrada es un récord personal → overlay ¡PR!
        return dispatchWindowEvent('orborix:fit.pr', {
          workoutId: msg.workoutId,
          exerciseId: msg.exerciseId,
          exerciseName: msg.exerciseName,
          kg: msg.kg,
          reps: msg.reps,
          metric: msg.metric,
        });
      case 'fit.plan.rework.offered':
        // Fit (F2d): el user pidió rearmar el plan de fondo → la app muestra la
        // card de oferta (proponer→confirmar; el server jamás auto-aplica).
        return dispatchWindowEvent(
          'orborix:fit.plan.rework.offered',
          msg.available
            ? {
                available: true,
                fromWeek: msg.fromWeek,
                remainingWeeks: msg.remainingWeeks,
              }
            : { available: false, reason: msg.reason },
        );
      default:
        // transition / task-update — consumed by shell at this layer.
        return;
    }
  };

  return { handleBinary, handleJson };
}

function dispatchWindowEvent(eventName: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch {
    /* SSR / non-browser noop */
  }
}

/**
 * Commit de la burbuja del bot viva al historial (con la traducción stashed,
 * si hay). IDEMPOTENTE: sin texto vivo es no-op — por eso los múltiples
 * caminos de commit (turn-end, entrada a thinking, tokens con responseId
 * nuevo, interrupted) pueden coexistir sin duplicar bubbles.
 */
function commitLiveBotBubble(deps: ServerHandlerDeps): void {
  if (!deps.assistantTextRef.current) return;
  const text = deps.assistantTextRef.current;
  const pendingTr = deps.pendingBotTranslationRef.current;
  deps.pendingBotTranslationRef.current = null;
  const pendingSrc = deps.pendingBotWebSourcesRef.current;
  deps.pendingBotWebSourcesRef.current = null;
  // Las fuentes pasan a la burbuja (VoiceMessage.sources); el chip "vivo" se
  // apaga en el mismo commit para que el de la burbuja ya cerrada tome la posta.
  deps.setLiveWebSources([]);
  // Snapshot del karaoke ANTES de que `clearKaraokeBuffer` (turn-end / nuevo
  // turno) borre el buffer: es lo que le da karaoke al replay de la burbuja.
  const alignment = snapshotKaraokeAlignment(deps.karaoke);
  deps.setMessages((m) => [
    ...m,
    {
      who: 'bot',
      text,
      ...(pendingTr ? { translation: pendingTr } : {}),
      ...(pendingSrc && pendingSrc.length > 0 ? { sources: pendingSrc } : {}),
      ...(alignment ? { alignment } : {}),
    },
  ]);
  deps.assistantTextRef.current = '';
  deps.setAssistantText('');
}

function handleState(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'state' }>,
): void {
  const prev = deps.prevStateRef.current;
  const next = msg.value;
  // Cronómetro de TTS al transicionar a `thinking` (LLM corre + TTS por
  // arrancar). Fallback: listening → speaking sin pasar por thinking.
  if (
    (prev !== 'thinking' && next === 'thinking') ||
    (prev === 'listening' && next === 'speaking')
  ) {
    deps.ttsStopwatchRef.current = Date.now();
  }
  // Dos turnos del bot seguidos (p.ej. el coach abrió y después tocás un seed
  // → pivote): si arranca un turno NUEVO (entra a `thinking`) y el bubble del
  // bot anterior todavía no se commiteó (su turn-end acústico no disparó porque
  // este turno se le adelantó), lo cerramos ACÁ. Así el nuevo turno abre un
  // bubble SEPARADO en vez de concatenar los dos mensajes en uno solo. Dos
  // turnos del bot = dos bubbles (comportamiento esperado).
  // Turno nuevo del coach: la cuenta de cuánto sonó arranca vacía y cerrada
  // hasta su primer chunk (`handleBinary`). Sin esto, un corte mientras piensa
  // leía el audio del turno ANTERIOR como si fuera de este, y el acuse del lab
  // (que suena en `thinking`) se sumaba al reply.
  if (prev !== 'thinking' && next === 'thinking') {
    deps.playerRef.current?.markTurnStart();
    deps.playerRef.current?.closeTurnAccounting();
  }
  if (prev !== 'thinking' && next === 'thinking' && deps.assistantTextRef.current) {
    commitLiveBotBubble(deps);
    deps.realtimeLiveResponseIdRef.current = null;
    clearKaraokeBuffer(deps.karaoke);
  }
  // NOTA: karaoke buffer NO se limpia acá aunque `state` salga de speaking.
  // Audio sigue drenando ~200-500ms más; la limpieza vive en `awaitIdle`
  // del `turn-end` (fin acústico real).
  // NO commiteamos el bot acá. El server emite `state:listening` cuando el
  // LLM termina de generar tokens, pero el audio del TTS sigue drenando en
  // el cliente varios segundos más. Si commiteábamos acá, el bubble pasaba
  // de partial→committed antes de tiempo: el karaoke se deshabilitaba y el
  // texto restante aparecía "de golpe" en vez de seguir revelándose
  // sincronizado al audio que continuaba sonando. El commit real ahora vive
  // en `handleTurnEnd` (awaitIdle resolve = fin acústico real).
  // Nuevo turno escuchando: limpiamos el partial transcript anterior.
  if (next === 'listening' && prev !== 'idle' && prev !== 'listening') {
    deps.setTranscript('');
  }
  // Texto del coach completo (el server emite `listening` cuando el LLM terminó
  // de generar tokens; el audio sigue drenando hasta `turn-end`). Si el turno
  // buscó en la web, exponemos las fuentes YA — así el chip aparece ni bien se
  // termina de escribir la burbuja, sin esperar el fin del audio/commit.
  if (next === 'listening' && deps.pendingBotWebSourcesRef.current) {
    deps.setLiveWebSources(deps.pendingBotWebSourcesRef.current);
  }
  deps.prevStateRef.current = next;
  deps.setState(next);
}

function handleTranscript(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'transcript' }>,
): void {
  if (msg.partial !== undefined) {
    const text = msg.partial;
    // Partial VACÍO = señal explícita de limpieza del server (el SttProcessor
    // lo emite al descartar un turno — turn-discard, guard anti-fantasma, o
    // flush de turno vacío post-tap). El server nunca emite partials vacíos
    // en otro contexto, así que no hay riesgo de borrar un bubble legítimo.
    if (text.trim() === '') {
      deps.setTranscript('');
      // Con un commit en vuelo, la limpieza significa que el turno del user
      // MURIÓ sin transcript (audio perdido / commit ignorado): la UI tiene
      // que enterarse o el user se queda mirando un envío que nunca sale.
      if (deps.commitPendingRef.current) deps.onTurnEmpty();
    } else {
      const meaningful = text.trim().replace(/[.,!?…-]+/g, '');
      if (meaningful.length >= MIN_PARTIAL_CHARS) {
        const trimmed = text.trim();
        // Guard contra partial residual idéntico al último final (re-aparece
        // el bubble live con texto ya committeado en `messages`).
        if (deps.lastFinalTranscriptRef.current === trimmed) return;
        deps.lastFinalTranscriptRef.current = null;
        deps.setTranscript(text);
      }
    }
  }
  if (msg.final !== undefined) {
    const finalText = msg.final;
    // El server confirmó el turno: el commit en vuelo llegó a puerto.
    deps.clearCommitPending();
    // Turno del user que se solapó con el coach todavía hablando (realtime, o
    // un final que entra antes del `turn-end` acústico): se marca para que el
    // render lo baje por debajo de la burbuja del coach que le ganó de mano al
    // commitear (ver `displayOrder`). Se LEE acá, no dentro del updater: el
    // updater corre recién en el flush de React, y para entonces los tokens
    // del turno SIGUIENTE ya pueden haber llenado el ref.
    const racedCoach = deps.assistantTextRef.current.length > 0;
    deps.setMessages((m) => {
      // Fase 28: en modo input=text, optimistic push del user msg ya
      // sucedió en `sendText`. El server emite `transcript.final` con el
      // mismo texto vía bypass STT — desduplicamos contra el último user.
      const last = m[m.length - 1];
      if (last && last.who === 'user' && last.text.trim() === finalText.trim()) {
        return m;
      }
      return [
        ...m,
        {
          who: 'user',
          text: finalText,
          // Sólo si esta sesión monta el side-runner: sin él no hay
          // `corrections.done` y el skeleton quedaría colgado (card #318).
          ...(deps.correctionsEnabledRef.current ? { correctionPending: true } : {}),
          ...(racedCoach ? { sentWhileCoachSpeaking: true } : {}),
        },
      ];
    });
    deps.setTranscript('');
    deps.lastFinalTranscriptRef.current = finalText.trim();
  }
}

function handleTokens(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'tokens' }>,
): void {
  // Modo realtime (F4b): el revelado va paced por la llegada de chunks
  // (aprox sincro con el audio — decisión #16) y keyeado por responseId. Un
  // delta de un ciclo NUEVO mientras la burbuja anterior sigue viva (solape
  // por barge-in: el turno N drena audio mientras N+1 ya streamea) commitea
  // la anterior — sin esto los dos turnos se concatenaban en un solo bubble.
  if (deps.realtimeModeRef.current && typeof msg.responseId === 'string') {
    const liveId = deps.realtimeLiveResponseIdRef.current;
    if (liveId !== null && liveId !== msg.responseId) {
      commitLiveBotBubble(deps);
    }
    deps.realtimeLiveResponseIdRef.current = msg.responseId;
  }
  if (deps.karaoke.tokensStartedAtMs == null) {
    deps.karaoke.tokensStartedAtMs = performance.now();
  }
  deps.onReplyText();
  deps.assistantTextRef.current = deps.assistantTextRef.current + msg.delta;
  deps.setAssistantText(deps.assistantTextRef.current);
}

/**
 * El texto del coach ya está completo: el server avisa apenas el LLM cierra su
 * respuesta, mientras la voz todavía se sintetiza y suena (la burbuja se cierra
 * recién al terminar de sonar, y el `turn-end` llega con el fin del audio, 1–2 s
 * después). Para quien quiera adelantarse: la charla asistida arma su respuesta
 * modelo acá, así está lista cuando el coach termina de hablar (Emmanuel, 14/10
 * y 16/10).
 */
function handleTokensDone(deps: ServerHandlerDeps): void {
  if (!deps.assistantTextRef.current.trim()) return;
  dispatchWindowEvent('orborix:coach.text-complete', { text: deps.assistantTextRef.current });
}

/**
 * Cierra el turno para el contador de settle y manda el acumulado al server.
 *
 * Va en `turn-end` porque es el único punto frío que ocurre una vez por turno
 * del coach y donde la ventana del turno ANTERIOR ya venció con certeza (pasó
 * un turno entero del usuario en el medio). Ver `closeSettleWindow`.
 *
 * En realtime NO se reporta: ahí el gate de settle se bypasea por diseño, así
 * que esos turnos no pueden morder y sumarlos al denominador diluiría el
 * número. El dato dice "de cada 100 turnos half-duplex".
 *
 * Best-effort de punta a punta: esto es diagnóstico y NO puede tirar abajo una
 * charla — si el socket no está o el `send` falla, se pierde el reporte y la
 * sesión sigue igual (el acumulado es cumulativo: el próximo turno lo repone).
 */
function reportSettleCounter(deps: ServerHandlerDeps): void {
  if (deps.realtimeModeRef.current) return;
  const counter = deps.settleCounterRef.current;
  closeSettleWindow(counter);
  const ws = deps.wsRef.current;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(
      encodeClientWsMessage({
        type: 'mic-settle-report',
        turns: counter.turns,
        bittenTurns: counter.bittenTurns,
        chunks: counter.chunks,
        droppedMs: Math.round(counter.droppedMs),
      }),
    );
  } catch {
    /* diagnóstico: nunca rompe la sesión */
  }
}

function handleTurnEnd(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'turn-end' }>,
): void {
  reportSettleCounter(deps);
  // El server ya mandó todo el audio del turno: un corte de acá en más sabe
  // que lo recibido es el turno entero.
  deps.turnAudioCompleteRef.current = true;
  deps.playerRef.current?.closeTurnAccounting();
  // Modo realtime (F4b): sin karaoke/alignment, el commit diferido por
  // `awaitIdle` no aplica — el texto ya se reveló completo con los chunks.
  // Commit INMEDIATO y keyeado por responseId: un turn-end de un ciclo viejo
  // (llegó después de que N+1 abriera burbuja) no pisa la burbuja nueva.
  // El settle + `playback-finished` siguen anclados al fin acústico real.
  if (deps.realtimeModeRef.current) {
    const liveId = deps.realtimeLiveResponseIdRef.current;
    const matchesLive =
      msg.responseId === undefined || liveId === null || liveId === msg.responseId;
    if (matchesLive) {
      commitLiveBotBubble(deps);
      deps.realtimeLiveResponseIdRef.current = null;
    }
    const player = deps.playerRef.current;
    if (!player?.awaitIdle) return;
    void player.awaitIdle().then(() => {
      deps.armMicSettle();
      deps.setIsAudioPlaying(false);
      const ws = deps.wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(encodeClientWsMessage({ type: 'playback-finished' }));
      } catch {
        /* never break the session */
      }
    });
    return;
  }
  const player = deps.playerRef.current;
  if (!player?.awaitIdle) return;
  void player.awaitIdle().then(() => {
    // Settle se arma desde el fin acústico REAL (no desde la transición
    // lógica del server a `listening`). Cubre decay del AEC + reverb.
    deps.armMicSettle();
    deps.setIsAudioPlaying(false);
    // Commit del bubble bot al historial. Demorado hasta acá (fin acústico
    // real, no la transición lógica a `listening`) para que el bubble bot
    // partial siga activo durante todo el drain del audio — el karaoke
    // sigue revelando texto sincronizado al audio que continúa sonando.
    // Si commiteábamos antes, el karaoke se desactivaba y el texto
    // restante aparecía "de golpe".
    commitLiveBotBubble(deps);
    clearKaraokeBuffer(deps.karaoke);
    const ws = deps.wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(encodeClientWsMessage({ type: 'playback-finished' }));
    } catch {
      /* never break the session */
    }
  });
}

/**
 * Barge-in del modo realtime: el server-VAD de OpenAI detectó al user
 * hablando encima del bot y canceló la respuesta. La cola local del player
 * tiene audio ya generado que NO corresponde seguir reproduciendo (la
 * generación va más rápida que el playback) — se corta acá. La burbuja viva
 * se commitea con lo que alcanzó a decirse; el resto del ciclo (turn-end
 * keyeado del response cancelado) es no-op gracias a la idempotencia.
 */
function handleInterrupted(deps: ServerHandlerDeps): void {
  if (!deps.realtimeModeRef.current) return;
  deps.playerRef.current?.reset();
  deps.setIsAudioPlaying(false);
  commitLiveBotBubble(deps);
  deps.realtimeLiveResponseIdRef.current = null;
}

function handleSessionStarted(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'session.started' }>,
): void {
  // A reconnect that landed on a NEW session row (a resume, not a reattach):
  // its turn numbering restarts, so the bubbles so far stop anchoring the new
  // session's per-turn messages (`retireTurnAnchors`).
  const previous = deps.sessionIdRef.current;
  if (previous !== null && previous !== msg.sessionId) {
    deps.setMessages((prev) => retireTurnAnchors(prev, previous));
  }
  deps.setSessionId(msg.sessionId);
  deps.setAudioRecording(msg.audioRecording === true);
  // «Server listo» para la app (también tras una reconexión, que reusa el id):
  // lo que la app le mande desde acá sí aterriza (charla asistida, encargo 99).
  dispatchWindowEvent('orborix:session.started', { sessionId: msg.sessionId });
  // ¿La sesión produce correcciones? Con el side-runner apagado (examen de
  // nivel, A0 sin misión, módulo sin runner) no marcamos el skeleton
  // "analizando…": nadie va a emitir el `corrections.done` que lo limpia.
  // Ausente = backend viejo → asumimos que sí (conducta previa).
  deps.correctionsEnabledRef.current = msg.corrections?.enabled !== false;
  // Re-envío del modo I/O no-default. El `mode.switch` que `onWsOpen` manda al
  // abrir el socket llega ANTES de que el server termine de armar la sesión: su
  // transport recién engancha el listener de mensajes al final del arranque, así
  // que ese frame se pierde en el aire (ni siquiera loguea — el handler nunca
  // corre). Medido 2026-09-09: abrir en `input:'text'` no devolvía `mode.changed`
  // y el turno quedaba trazado como `io_input: voice`. `session.started` es la
  // señal de "server listo" (se emite después de construir el AgentSession), así
  // que acá el switch sí aterriza. Idempotente de los dos lados: `switchMode`
  // corta si el modo ya coincide.
  const io = deps.ioModeRef.current;
  if (io.input !== 'voice' || io.output !== 'voice') {
    const ws = deps.wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(encodeClientWsMessage({ type: 'mode.switch', mode: io }));
      } catch {
        /* socket puede haberse ido — el modo local ya es correcto */
      }
    }
  }
  // Modo de conversación resuelto server-side (plan realtime). El ref gatea el
  // bypass half-duplex del mic desde el próximo chunk PCM; el setter expone el
  // modo a la app (UX duplex de Fase 4). Ausente = classic (backend viejo).
  const mode = msg.conversationMode === 'realtime' ? 'realtime' : 'classic';
  deps.realtimeModeRef.current = mode === 'realtime';
  deps.setConversationMode(mode);
  if (msg.translation) {
    deps.setTranslationEnabledState(msg.translation.enabled);
    deps.setTranslationTargetLangState(msg.translation.targetLang.toLowerCase());
  }
  if (msg.openerAssistantText && msg.openerAssistantText.trim().length > 0) {
    const plain = msg.openerAssistantText;
    const openerContent = msg.openerAssistantContent;
    deps.setMessages((prev) => [
      ...prev,
      {
        who: 'bot',
        text: plain,
        ...(openerContent ? { content: openerContent } : {}),
      },
    ]);
  }
}

function handlePronunciationReport(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'pronunciation_report' }>,
): void {
  const reportQuals = pickQualifications(msg.qualifications);
  const report: PronunciationReportLite = {
    overall: msg.overall,
    words: msg.words.map((w) => {
      const wq = pickQualifications(w.qualifications);
      return {
        word: w.word,
        accuracy: w.accuracy,
        errorType: w.errorType,
        phonemes: w.phonemes.map((p) => ({
          ipa: p.ipa,
          accuracy: p.accuracy,
          ...(p.soundMostLike != null ? { soundMostLike: p.soundMostLike } : {}),
          ...(p.stressLevel != null ? { stressLevel: p.stressLevel } : {}),
          ...(p.stressScore != null ? { stressScore: p.stressScore } : {}),
        })),
        ...(wq ? { qualifications: wq } : {}),
      };
    }),
    ...(reportQuals ? { qualifications: reportQuals } : {}),
  };
  deps.setMessages((m) => applyPronunciationReport(m, msg.userText, report));
}

function handlePronunciationCorrection(
  deps: ServerHandlerDeps,
  msg: Extract<ServerWsMessage, { type: 'pronunciation_correction' }>,
): void {
  const wrQuals = pickQualifications(msg.wordReport?.qualifications);
  const wr = msg.wordReport
    ? {
        accuracy: msg.wordReport.accuracy,
        errorType: msg.wordReport.errorType,
        phonemes: msg.wordReport.phonemes.map((p) => ({
          ipa: p.ipa,
          accuracy: p.accuracy,
          ...(p.soundMostLike != null ? { soundMostLike: p.soundMostLike } : {}),
          ...(p.stressLevel != null ? { stressLevel: p.stressLevel } : {}),
          ...(p.stressScore != null ? { stressScore: p.stressScore } : {}),
        })),
        ...(wrQuals ? { qualifications: wrQuals } : {}),
      }
    : null;
  const pc: PronunciationCorrection = {
    word: msg.word,
    saidIpa: msg.saidIpa,
    targetIpa: msg.targetIpa,
    tip: msg.tip,
    detail: msg.detail ?? null,
    ...(wr ? { wordReport: wr } : {}),
  };
  deps.setMessages((m) => applyPronunciationCorrection(m, msg.userText ?? null, msg.word, pc));
}
